/**
 * src/hooks/useFullScreenDrop.ts
 * =============================================================================
 * Hook que escucha drag-and-drop en TODA la ventana (no solo un área concreta).
 * Emite un flag `isDragging` mientras el cursor lleva un archivo encima del
 * viewport y dispara `onFile` cuando se suelta uno del tipo correcto.
 *
 * Decisión de diseño:
 *  - Usamos un contador (`dragCounter`) para manejar correctamente los eventos
 *    `dragenter`/`dragleave` que disparan los hijos de la página. Sin el
 *    contador, pasar el cursor sobre un input mientras arrastras un archivo
 *    haría parpadear el overlay como loco.
 *  - `onFile` se guarda en un ref para no re-atar listeners cada vez que el
 *    consumidor pase una nueva función inline.
 *  - Filtramos por `e.dataTransfer.types.includes('Files')` para no activar el
 *    overlay cuando el usuario arrastra texto o un link del propio navegador.
 *
 * Ejemplo:
 *   const { isDragging } = useFullScreenDrop({
 *     accept: 'application/pdf',
 *     onFile: (file) => setFile(file),
 *   })
 * =============================================================================
 */

import { useEffect, useRef, useState } from 'react'

interface Options {
  /** MIME type que aceptamos (ej. `application/pdf`). Otros tipos se ignoran. */
  accept: string
  /** Callback que recibe el archivo soltado y válido. */
  onFile: (file: File) => void
}

export function useFullScreenDrop({ accept, onFile }: Options): { isDragging: boolean } {
  const [isDragging, setIsDragging] = useState(false)
  // El consumidor puede pasar una nueva función en cada render — guardamos
  // la referencia en un ref para no tirar y rehacer los listeners.
  const onFileRef = useRef(onFile)
  useEffect(() => {
    onFileRef.current = onFile
  }, [onFile])

  useEffect(() => {
    let dragCounter = 0

    function handleEnter(e: DragEvent) {
      // Solo activamos si lo que se arrastra son archivos (no texto/URLs).
      if (!e.dataTransfer?.types || !Array.from(e.dataTransfer.types).includes('Files')) {
        return
      }
      e.preventDefault()
      dragCounter++
      if (dragCounter === 1) setIsDragging(true)
    }

    function handleLeave(e: DragEvent) {
      e.preventDefault()
      dragCounter = Math.max(0, dragCounter - 1)
      if (dragCounter === 0) setIsDragging(false)
    }

    function handleOver(e: DragEvent) {
      // Imprescindible: sin preventDefault aquí, el navegador NO dispara
      // el evento `drop` posterior (comportamiento default = abrir el archivo).
      e.preventDefault()
    }

    function handleDrop(e: DragEvent) {
      e.preventDefault()
      dragCounter = 0
      setIsDragging(false)
      const files = e.dataTransfer?.files
      if (!files || files.length === 0) return
      const f = files[0]
      if (f && f.type === accept) {
        onFileRef.current(f)
      }
    }

    window.addEventListener('dragenter', handleEnter)
    window.addEventListener('dragleave', handleLeave)
    window.addEventListener('dragover', handleOver)
    window.addEventListener('drop', handleDrop)
    return () => {
      window.removeEventListener('dragenter', handleEnter)
      window.removeEventListener('dragleave', handleLeave)
      window.removeEventListener('dragover', handleOver)
      window.removeEventListener('drop', handleDrop)
    }
  }, [accept])

  return { isDragging }
}
